'use client'

import { toHomePage } from '@/feature/account/services/account.routing'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { ensureLeadingSlash } from '@/lib/utils/link-utils'
import { Icon } from '@/ui/icons/icon/Icon'
import { useMemo } from 'react'

import { Link } from '@/ui/links/link/Link.client'

const ICON_SIZE = 'sm'
const ICON_MARGIN = 'mx-1'

export type BreadcrumbCrumb = {
  name: string
  urlKey?: string | null
}

export type BreadcrumbProps = {
  crumbs: BreadcrumbCrumb[] | null | undefined
  id?: string
  'data-testid'?: string
}

export function Breadcrumb({
  crumbs,
  id,
  'data-testid': dataTestId,
}: BreadcrumbProps): React.ReactElement {
  const { t } = useTranslation(['generic', 'breadcrumb'])

  const processedBreadcrumbs = useMemo(() => {
    const validBreadcrumbs = crumbs ?? []

    const homepageEntry: BreadcrumbCrumb = {
      name: t('generic:home'),
      urlKey: toHomePage,
    }

    return [homepageEntry, ...validBreadcrumbs]
  }, [crumbs, t])

  /*
   * The last element of the breadcrumbs array is the current page
   * in mobile, we need to display the closest ancestor so last element -1
   */
  const closestParent = useMemo(() => {
    return processedBreadcrumbs.length >= 2
      ? processedBreadcrumbs[processedBreadcrumbs.length - 2]
      : null
  }, [processedBreadcrumbs])

  const navProps = {
    id,
    'data-testid': dataTestId,
    'aria-label': t('breadcrumb:ariaLabel'),
  }

  return (
    <nav {...navProps} className="embed:hidden">
      <ol
        className="my-3 hidden flex-wrap items-center desktop:flex"
        data-testid="breadcrumb/desktop"
      >
        {processedBreadcrumbs.map((breadcrumb, i) => {
          const isLast = i === processedBreadcrumbs.length - 1

          const href = breadcrumb.urlKey ? ensureLeadingSlash(breadcrumb.urlKey) : null

          return (
            <li
              key={breadcrumb.urlKey ?? `${breadcrumb.name}`}
              className="flex items-center"
              aria-current={isLast ? 'page' : undefined}
            >
              {i > 0 && <Icon.ChevronRight className={ICON_MARGIN} size={ICON_SIZE} />}
              {!isLast && href ? (
                <Link
                  variant="free"
                  href={href}
                  className="block leading-light"
                  underlined="invert"
                  trackingData={{
                    event: 'gaEvent',
                    eventCategory: "fil d'ariane",
                    eventAction: 'clic',
                    eventLabel: breadcrumb.name,
                  }}
                >
                  {breadcrumb.name}
                </Link>
              ) : (
                <span className="leading-light">{breadcrumb.name}</span>
              )}
            </li>
          )
        })}
      </ol>

      {closestParent?.urlKey && (
        <ol className="desktop:hidden">
          <li className="my-2 flex items-center" data-testid="breadcrumb/mobile">
            <Link
              variant="free"
              href={ensureLeadingSlash(closestParent.urlKey)}
              className="relative"
              underlined="invert"
              icon={<Icon.ChevronLeft size="md" />}
            >
              {closestParent.name}
            </Link>
          </li>
        </ol>
      )}
    </nav>
  )
}
